import type { ReactNode } from "react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ThemeModeProvider } from "@/theme/theme-provider";
import { Toaster } from "@/components/feedback/toaster";
import { ConfirmDialogHost } from "@/components/feedback/confirm-dialog";
import { useBranding } from "@/api/settings";
import { PERSIST_BUSTER, queryClient, queryPersister } from "./query-client";

/** Re-themes the app once `/api/branding` resolves (colours + fonts). */
function BrandedTheme({ children }: { children: ReactNode }) {
  const branding = useBranding().data;
  return (
    <ThemeModeProvider branding={branding}>
      {children}
      <Toaster />
      <ConfirmDialogHost />
    </ThemeModeProvider>
  );
}

/**
 * Global providers, outermost to innermost: data (persisted) → theme (branded)
 * → app. There is exactly one `ThemeModeProvider` (inside `BrandedTheme`) —
 * nesting two would mean MUI emits the palette CSS variables only from the
 * outer, unbranded theme, so tenant colours never reach the app.
 * `BrandedTheme` renders the brand defaults until `/api/branding` resolves.
 */
export function Providers({ children }: { children: ReactNode }) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        maxAge: 24 * 60 * 60_000,
        buster: PERSIST_BUSTER,
        dehydrateOptions: {
          shouldDehydrateQuery: (q) => q.state.status === "success",
        },
      }}
    >
      <BrandedTheme>{children}</BrandedTheme>
    </PersistQueryClientProvider>
  );
}
